// src/features/projects/briefing/useLandingWeekly.js
//
// Contributor/decision counts for the compact landing strip and alert chip.
// Always pinned to the server-owned current week (from useCurrentProjectWeek),
// never an archive or demo week. Stale responses are ignored and aborted.

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { projectIdsKey } from "../../../data/projectHubClient.js";
import { weeklyCountsSnapshot, readWeeklyCounts } from "../../../services/projectWeeklyService.js";
import { invalidateProjectWeekly, subscribeWeeklyCache, weeklyCacheGeneration } from "../../../services/projectWeeklyCache.js";

export function useLandingWeekly(projectIds, weekState, refreshToken = 0, authIdentity = "unbound") {
  const idsKey = projectIdsKey(projectIds);
  const generation = useSyncExternalStore(subscribeWeeklyCache, weeklyCacheGeneration);
  const [clock, setClock] = useState(0);
  const [state, setState] = useState({ key: null, data: null, error: null });
  const previousRefresh = useRef(refreshToken);
  // Discovery/overview is supplemental, not a prerequisite. If it confirms a
  // different current week, discard the old evidence immediately and reconcile.
  const confirmedWeek = weekState?.currentWeek || weekState?.data?.currentWeek || null;
  const baseSnapshot = weeklyCountsSnapshot(projectIds, authIdentity, null);
  const week = baseSnapshot && confirmedWeek > baseSnapshot.weekDate ? confirmedWeek : null;
  const identity = JSON.stringify([idsKey, authIdentity, week]);
  const reqKey = `${identity}|${confirmedWeek}|${generation}|${clock}`;
  const snapshot = weeklyCountsSnapshot(projectIds, authIdentity, week);
  useEffect(() => {
    if (previousRefresh.current !== refreshToken) {
      previousRefresh.current = refreshToken;
      invalidateProjectWeekly();
    }
  }, [refreshToken]);
  useEffect(() => {
    const tick = () => setClock(n => n + 1);
    const timer = window.setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", tick); };
  }, []);

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    if (ids.length === 0) return undefined;
    let active = true;
    readWeeklyCounts(ids, authIdentity, week)
      .then((data) => {
        if (!active) return;
        setState({ key: reqKey, data, error: null });
      })
      .catch((err) => { if (active) setState({ key: reqKey, data: null, error: err?.message || "Could not load weekly counts." }); });
    // Shared in-flight reads survive unmount; only this subscription is dropped.
    return () => { active = false; };
  }, [reqKey]);

  const retryFn = invalidateProjectWeekly;
  if (!idsKey) return { empty: true, loading: false, error: null, data: null, weekDate: confirmedWeek, retry: retryFn };
  const current = state.key === reqKey ? state : null;
  const settled = snapshot || current?.data || null;
  const valid = settled && Date.now() < Date.parse(`${settled.weekDate}T00:00:00Z`) + 7 * 86400000
    && (!confirmedWeek || settled.weekDate === confirmedWeek || settled.currentWeek === settled.weekDate);
  const mismatch = settled && confirmedWeek && settled.weekDate !== confirmedWeek
    && settled.currentWeek !== settled.weekDate;
  const error = current?.error || (mismatch ? "Weekly counts did not match the current week. Please retry." : null);
  const data = !error && valid ? settled : null;
  return { empty: false, loading: !data && !error, error,
    data, weekDate: data?.weekDate || confirmedWeek, retry: retryFn };
}
